/**
 * The avatar as a file a garment designer can load: CLO3D, Style3D, Blender.
 *
 * The model service returns the STAR mesh the way its camera sees it, in
 * metres with Y pointing down, and the viewer turns it upright on render
 * (rotation.x = π, rotation.y = π in 3DModel.tsx). Those two half turns are
 * one half turn about Z, so the export does the same thing to the vertices:
 * x and y change sign, z stays. A half turn is a rotation, not a mirror, so
 * left stays left and the faces keep winding outwards.
 *
 * CLO's "Load as Avatar" wants Y up, outward normals and an A- or T-pose; the
 * scan carries the customer's capture pose, which is an A-pose. It then
 * places its own arrangement points, which works best with the feet on the
 * floor and the body centred, so the export does that as well.
 *
 * The raw mesh is not quite the customer's size, which is why measureObj
 * carries a scale. The export is sized to the height measured for the scan
 * instead, crown to soles, so a designer reads the same stature the app shows.
 */

const UNIT = 'cm';

/** Heights outside this range are not a person's, and the mesh keeps its own size. */
const PLAUSIBLE_CM = { min: 50, max: 250 };

const usableHeight = (heightCm?: number): heightCm is number =>
  typeof heightCm === 'number' &&
  Number.isFinite(heightCm) &&
  heightCm >= PLAUSIBLE_CM.min &&
  heightCm <= PLAUSIBLE_CM.max;

const fmt = (n: number) => {
  const s = n.toFixed(4);
  return s === '-0.0000' ? '0.0000' : s;
};

/**
 * The stored mesh (base64 OBJ, as the API returns it) as an upright OBJ in
 * centimetres: Y up, facing +Z, centred over the origin, feet at y = 0, and
 * `heightCm` tall when that is a plausible height.
 */
export function cloAvatarObj(objBase64: string, heightCm?: number): string {
  const lines = atob(objBase64).split(/\r?\n/);

  // Turned, still in metres. Kept by line index so everything else passes
  // through in place.
  const turned = new Map<number, [number, number, number]>();
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;

  lines.forEach((line, i) => {
    const parts = line.trim().split(/\s+/);
    if (parts[0] !== 'v' || parts.length < 4) return;
    const [x, y, z] = parts.slice(1, 4).map(Number);
    const p: [number, number, number] = [-x, -y, z];
    turned.set(i, p);
    minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]);
    minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]);
    minZ = Math.min(minZ, p[2]); maxZ = Math.max(maxZ, p[2]);
  });

  if (turned.size === 0) {
    throw new Error('Avatar has no vertices');
  }

  const standing = maxY - minY;
  const cmPerUnit = usableHeight(heightCm) ? heightCm / standing : 100;
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;

  const out = lines.map((line, i) => {
    const p = turned.get(i);
    if (p) {
      return `v ${fmt((p[0] - cx) * cmPerUnit)} ${fmt((p[1] - minY) * cmPerUnit)} ${fmt((p[2] - cz) * cmPerUnit)}`;
    }
    const parts = line.trim().split(/\s+/);
    if (parts[0] === 'vn' && parts.length >= 4) {
      // A uniform scale leaves a normal's direction alone; only the turn applies.
      const [nx, ny, nz] = parts.slice(1, 4).map(Number);
      return `vn ${fmt(-nx)} ${fmt(-ny)} ${fmt(nz)}`;
    }
    return line;
  });

  const tall = (standing * cmPerUnit).toFixed(1);
  const header =
    `# Sizewall avatar. Y up, facing +Z, centimetres, ${tall} cm tall, feet at y = 0. ` +
    `In CLO3D: File > Import > OBJ, Load as Avatar, unit cm, ` +
    `Auto Add Arrangement Points and Auto Create Fitting Suit on.`;
  return [header, ...out].join('\n');
}

/** `sizewall-avatar-2026-11-04-cm.obj`, by the date of the scan. */
export function avatarFileName(createdAt: string): string {
  const d = new Date(createdAt);
  const day = Number.isNaN(d.getTime()) ? '' : `${d.toISOString().slice(0, 10)}-`;
  return `sizewall-avatar-${day}${UNIT}.obj`;
}

/** Hands the browser the file to save. */
export function downloadAvatar(objBase64: string, heightCm: number | undefined, createdAt: string): void {
  const blob = new Blob([cloAvatarObj(objBase64, heightCm)], { type: 'model/obj' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = avatarFileName(createdAt);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick: Safari cancels a download whose URL goes first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
